/**
 * Экран «Цели» (раздел 8, пункт 4).
 *
 * Смысл экрана — сделать главную цель осязаемой: сколько сейчас, сколько
 * надо, и когда это случится по текущему темпу. Все цифры приходят с
 * сервера: e1RM, целевой вес, прогнозная дата, темп кг/мес и R² считает
 * движок при каждом запросе (раздел 2, принцип «цифры первичны»). Клиент
 * не считает ничего, кроме доли «текущее от целевого» для полосы прогресса —
 * это оформление уже полученных чисел, а не новая метрика.
 *
 * Честность (раздел 2, принцип 3 и раздел 13, риск «прогноз врёт на малых
 * данных»): если движок сказал `has_forecast: false`, показывается его
 * `reason` как есть и никакой даты. Смягчать («примерно», «около») нельзя —
 * движок уже решил, что данных мало, и подмена его решения вежливой
 * формулировкой вернула бы ровно тот риск, ради которого порог по R² и вводился.
 */

import { useMemo, useState } from 'react'

import { api, NetworkError } from '../api/client'
import type {
  Forecast,
  Goal,
  GoalOption,
  GoalTheme as GoalThemeOut,
  Today,
} from '../api/types'
import { haptic } from '../auth/telegram'
import {
  ConfirmButton,
  Empty,
  ErrorBox,
  Loader,
  Stepper,
  formatDate,
  formatWeight,
} from '../components/ui'
import { useExercises } from '../api/exercises'
import { useAsync } from '../hooks'
import { CACHE_TODAY, readCache } from '../offline/db'

/**
 * Целевой вес по умолчанию: главная цель из спецификации — жим 100 кг.
 *
 * Используется ровно в одном случае: сервер не отдал по движению ни темпа, ни
 * текущего максимума, ни минимального веса. Во всех остальных поле
 * отталкивается от цифр сервера — см. `startingTarget`.
 */
const DEFAULT_TARGET = 100

/**
 * Границы целевого веса. Ровно те же, что у сервера (`GoalIn`: 0 < x ≤ 500) —
 * свой, более узкий коридор клиент придумывать не вправе: он молча запретил бы
 * законную цель (жим ногами на 400 кг) там, где движок её принимает.
 */
const TARGET_MIN = 2.5
const TARGET_MAX = 500

/**
 * С какого веса открывается поле цели.
 *
 * Порядок ровно такой: предложение движка (посчитано по собственному темпу
 * человека) → текущий максимум → минимальный вес снаряда (пустой гриф, первая
 * гантель, нижняя плитка стека). Все три — числа СЕРВЕРА. Своё правдоподобное
 * число клиент не выдумывает: «сто килограммов» на подъёме на бицепс — это не
 * подсказка, а насмешка.
 */
function startingTarget(option: GoalOption): number {
  if (option.suggested_target !== null) return option.suggested_target
  if (option.current_e1rm !== null) return option.current_e1rm
  return option.min_weight > 0 ? option.min_weight : DEFAULT_TARGET
}

/** Ответ «Сегодня» вместе с пометкой, свежий он или поднят из кеша. */
interface TodayView {
  data: Today
  fresh: boolean
}

/**
 * Ближайшая тренировка — источник названий упражнений и «сегодня» сервера.
 *
 * При обрыве связи берётся последний сохранённый ответ (тот же кеш, что
 * пишет экран «Сегодня»): названия из него не устаревают. Отказ сервера
 * кешем не подменяется — сервер ответил, значит связь есть.
 */
async function loadToday(): Promise<TodayView> {
  try {
    return { data: await api.get<Today>('/today'), fresh: true }
  } catch (e) {
    if (e instanceof NetworkError) {
      const cached = await readCache<Today>(CACHE_TODAY).catch(() => null)
      if (cached) return { data: cached.value, fresh: false }
    }
    throw e
  }
}

export default function Goals() {
  const goals = useAsync(() => api.get<Goal[]>('/goals'), [])
  // Отдельным запросом, а не в одном `Promise.all` с целями: названия
  // упражнений — украшение, и падение `/today` не должно прятать цели.
  const today = useAsync(loadToday, [])

  const [creating, setCreating] = useState(false)
  const [busyGoalId, setBusyGoalId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const blocks = today.data?.data.session?.blocks
  const names = useMemo(() => {
    const map = new Map<number, string>()
    for (const block of blocks ?? []) map.set(block.exercise.id, block.exercise.name)
    return map
  }, [blocks])

  // Полный справочник одним кешируемым запросом. Раньше имя каждой цели
  // добиралось через /progress/{id} — а это регрессия и прогноз ради одной
  // строки текста.
  const catalog = useExercises()

  /** Название упражнения. Не нашли нигде — честно говорим номер. */
  const nameOf = (exerciseId: number): string =>
    names.get(exerciseId) ?? catalog.name(exerciseId)

  const openGoals = (goals.data ?? []).filter((g) => g.status === 'open')
  const closedGoals = (goals.data ?? []).filter((g) => g.status !== 'open')

  /** Сменить статус цели. Сервер пересчитает всё сам, поэтому — reload. */
  async function changeStatus(goal: Goal, status: 'achieved' | 'abandoned') {
    // Второе нажатие, пока первое в полёте, отправило бы второй PATCH и могло
    // бы переписать статус наоборот — «отказ» поверх «достигнута».
    if (busyGoalId !== null) return
    setBusyGoalId(goal.id)
    setActionError(null)
    try {
      await api.patch<Goal>(`/goals/${goal.id}`, { status })
      haptic(status === 'achieved' ? 'heavy' : 'light')
      await goals.reload()
    } catch (e) {
      setActionError(describe(e, 'Не удалось изменить цель'))
    } finally {
      setBusyGoalId(null)
    }
  }

  // Первая загрузка — Loader на весь экран. Перезапрос после действия —
  // нет: экран не должен мигать спиннером и терять раскрытую форму.
  if (goals.loading && goals.data === null) {
    return (
      <div className="screen">
        <h1>Цели</h1>
        <Loader />
      </div>
    )
  }

  if (goals.error && goals.data === null) {
    return (
      <div className="screen">
        <h1>Цели</h1>
        <ErrorBox error={goals.error} onRetry={() => void goals.reload()} />
      </div>
    )
  }

  const nothingAtAll = openGoals.length === 0 && closedGoals.length === 0

  return (
    <div className="screen">
      <h1>Цели</h1>
      <p className="muted" style={{ margin: '0 0 16px' }}>
        Цель — это вес, к которому ты идёшь. Из неё же строится прогноз даты выхода.
        Целей может быть несколько: руки, плечи, подтягивания — по одной на движение.
      </p>

      {/* Список не обновился, но старый ответ на экране остался. Молчать об
          этом нельзя: человек решит, что видит свежие цифры. */}
      {goals.error !== null && goals.data !== null && (
        <div className="flag warning">
          Список не обновился — на экране данные из прошлого ответа сервера.
        </div>
      )}

      {actionError && <div className="flag critical">{actionError}</div>}

      {nothingAtAll && !creating && (
        <>
          <Empty
            title="Целей пока нет"
            hint="Поставь цель — и приложение начнёт считать, когда ты выйдешь на этот вес по текущему темпу. Без цели прогнозировать нечего: дата берётся из неё."
          />
          <button className="btn primary wide big" onClick={() => setCreating(true)}>
            Поставить первую цель
          </button>
        </>
      )}

      {openGoals.map((goal) => (
        <GoalCard
          key={goal.id}
          goal={goal}
          name={nameOf(goal.exercise_id)}
          busy={busyGoalId === goal.id}
          onAchieved={() => void changeStatus(goal, 'achieved')}
          onAbandoned={() => void changeStatus(goal, 'abandoned')}
        />
      ))}

      {!nothingAtAll && !creating && (
        <button className="btn wide" onClick={() => setCreating(true)}>
          Новая цель
        </button>
      )}

      {creating && (
        <NewGoalForm
          openGoals={openGoals}
          nameOf={nameOf}
          // Нижняя граница календаря — только из СВЕЖЕГО ответа сервера:
          // «сегодня» из кеша может быть вчерашним, и поле разрешило бы дату,
          // которую сервер всё равно отвергнет.
          minDate={today.data?.fresh ? today.data.data.date : null}
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await goals.reload()
          }}
        />
      )}

      {closedGoals.length > 0 && (
        <>
          <h2>Закрытые цели</h2>
          {closedGoals.map((goal) => (
            <ClosedGoalCard key={goal.id} goal={goal} name={nameOf(goal.exercise_id)} />
          ))}
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Карточка активной цели                                                      */
/* -------------------------------------------------------------------------- */

function GoalCard({
  goal,
  name,
  busy,
  onAchieved,
  onAbandoned,
}: {
  goal: Goal
  name: string
  busy: boolean
  onAchieved: () => void
  onAbandoned: () => void
}) {
  // Доля от цели. Считать её здесь можно: это отношение двух уже полученных
  // от сервера чисел, а не новая метрика (движок ничего подобного не отдаёт).
  const share =
    goal.current_e1rm !== null && goal.target_1rm > 0
      ? (goal.current_e1rm / goal.target_1rm) * 100
      : null

  // Вниз, а не к ближайшему: 99.6% округлилось бы до «100%» рядом с открытой
  // целью, а взятой движок считает её только при current >= target — экран
  // объявил бы цель достигнутой раньше движка.
  const percent = share === null ? null : Math.floor(share)

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 10 }}>
        <b>{name}</b>
        {/* Больше сотни не прячем: заниженная цифра при перевыполнении была бы
            враньём. Обрезаем только полосу — она вылезла бы за дорожку. */}
        <span className="faint tnum">{percent === null ? '—' : `${percent}%`}</span>
      </div>

      <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
        <span className="big-number">{formatWeight(goal.current_e1rm)}</span>
        <span className="muted">
          из {formatWeight(goal.target_1rm)} кг
        </span>
      </div>
      <div className="faint" style={{ marginTop: -2 }}>
        текущий расчётный максимум на одно повторение
      </div>

      <div className="goal-bar" aria-hidden="true">
        <div className="goal-bar-fill" style={{ width: `${Math.min(100, share ?? 0)}%` }} />
      </div>

      {goal.current_e1rm === null && (
        <div className="faint" style={{ marginBottom: 10 }}>
          Максимум пока не посчитан — он появится после первых рабочих подходов в этом
          движении.
        </div>
      )}

      <ForecastLine forecast={goal.forecast} />

      {goal.target_date && (
        <div className="faint" style={{ marginTop: 8 }}>
          Твой срок: {formatGoalDate(goal.target_date)}
        </div>
      )}

      <div className="row" style={{ marginTop: 14 }}>
        <button
          className="btn primary"
          style={{ flex: 1 }}
          disabled={busy}
          onClick={onAchieved}
        >
          Достигнута
        </button>
        <ConfirmButton
          className="btn danger"
          confirmLabel="Точно отказаться?"
          onConfirm={onAbandoned}
        >
          Отказаться
        </ConfirmButton>
      </div>
    </div>
  )
}

/** Закрытая цель: без прогноза и без кнопок — это уже история, а не план. */
function ClosedGoalCard({ goal, name }: { goal: Goal; name: string }) {
  return (
    <div className="card tight dim">
      <div className="row between">
        <span>
          {name} · {formatWeight(goal.target_1rm)} кг
        </span>
        <span className={`pill ${goal.status === 'achieved' ? 'ok' : ''}`}>
          {goal.status === 'achieved' ? 'взята' : 'отказ'}
        </span>
      </div>
    </div>
  )
}

/**
 * Строка прогноза.
 *
 * Три разных состояния, и ни одно не прячется (раздел 2, принцип 3):
 * прогноза нет вовсе, движок отказался его строить, движок его построил.
 */
function ForecastLine({ forecast }: { forecast: Forecast | null }) {
  if (forecast === null) {
    return (
      <div className="faint">Прогноз появится, когда наберётся история.</div>
    )
  }

  if (!forecast.has_forecast) {
    // Причина показывается дословно: движок формулирует её сам и знает,
    // почему отказался. Дату в этом случае не показываем вообще.
    return <div className="flag info">{forecast.reason}</div>
  }

  return (
    <div className="goal-forecast">
      {forecast.projected_date && (
        <div>
          Выход ориентировочно <b>{formatGoalDate(forecast.projected_date)}</b>
        </div>
      )}
      {forecast.rate_kg_per_month !== null && (
        <div className="muted">
          Темп: {formatWeight(forecast.rate_kg_per_month)} кг/мес
        </div>
      )}
      <div className="faint">достоверность R² = {forecast.r_squared.toFixed(2)}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Создание цели                                                               */
/* -------------------------------------------------------------------------- */

function NewGoalForm({
  openGoals,
  nameOf,
  minDate,
  onCancel,
  onCreated,
}: {
  openGoals: Goal[]
  nameOf: (exerciseId: number) => string
  minDate: string | null
  onCancel: () => void
  onCreated: () => Promise<void>
}) {
  // Справочник для целей приходит отдельной ручкой и вместе с цифрами по
  // каждому движению: текущий максимум, предложенная планка и объяснение,
  // откуда она взялась. Собрать это на клиенте нельзя — считает движок.
  const options = useAsync(() => api.get<GoalThemeOut[]>('/goals/options'), [])
  const themes = options.data ?? []

  const [themeCode, setThemeCode] = useState<string | null>(null)
  const [picked, setPicked] = useState<GoalOption | null>(null)
  const [target, setTarget] = useState<number>(DEFAULT_TARGET)
  const [date, setDate] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const theme = themes.find((t) => t.code === themeCode) ?? themes[0] ?? null

  /** Выбрали движение — поле цели встаёт на цифру сервера, а не на 100 кг. */
  function choose(option: GoalOption) {
    setPicked(option)
    setTarget(startingTarget(option))
  }

  // Сервер закрывает прежнюю цель по тому же упражнению сам. Человек обязан
  // узнать об этом ДО нажатия, а не после — иначе он потеряет цель молча.
  const conflicting =
    picked === null ? undefined : openGoals.find((g) => g.exercise_id === picked.exercise_id)

  async function submit() {
    if (picked === null) return
    setBusy(true)
    setError(null)
    try {
      await api.post<Goal>('/goals', {
        exercise_id: picked.exercise_id,
        target_1rm: target,
        target_date: date === '' ? null : date,
      })
      haptic('medium')
      await onCreated()
    } catch (e) {
      setError(describe(e, 'Не удалось поставить цель'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 12 }}>
        <b>Новая цель</b>
        <button className="btn ghost" style={{ minHeight: 40, padding: '0 12px' }} onClick={onCancel}>
          Отмена
        </button>
      </div>

      {options.loading && options.data === null && <Loader label="Собираю справочник…" />}

      {options.error !== null && options.data === null && (
        <ErrorBox error={options.error} onRetry={() => void options.reload()} />
      )}

      {theme !== null && (
        <>
          <div className="faint" style={{ marginBottom: 6 }}>Что качаем</div>
          <div className="goal-picker">
            {themes.map((t) => (
              <button
                key={t.code}
                type="button"
                className={`pill ${t.code === theme.code ? 'accent' : ''}`}
                onClick={() => {
                  setThemeCode(t.code)
                  setPicked(null)
                }}
              >
                {t.title}
              </button>
            ))}
          </div>
          <div className="faint" style={{ marginTop: 6 }}>{theme.hint}</div>

          <div className="faint" style={{ margin: '14px 0 6px' }}>Движение</div>
          {theme.options.length === 0 ? (
            <div className="faint">
              В этой теме нечего предложить: движения убраны из выбора или исключены
              из-за боли.
            </div>
          ) : (
            <div className="goal-moves">
              {theme.options.map((option) => (
                <MovementRow
                  key={option.exercise_id}
                  option={option}
                  selected={picked?.exercise_id === option.exercise_id}
                  onPick={() => choose(option)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {picked !== null && (
        <>
          <div style={{ margin: '16px 0' }}>
            <Stepper
              value={target}
              onChange={setTarget}
              step={2.5}
              min={TARGET_MIN}
              max={TARGET_MAX}
              unit="кг"
              decimals={1}
              label={
                picked.external_load_only
                  ? 'Целевой вес СВЕРХ собственного на одно повторение'
                  : 'Целевой вес на одно повторение'
              }
            />
          </div>

          {/* Откуда взялась цифра в поле — словами движка. Это не украшение:
              предложенная планка посчитана по собственному темпу человека, а
              когда темпа нет, он обязан знать, что цифру ставит сам. */}
          {picked.basis !== '' && (
            <div className="faint" style={{ marginTop: -6, marginBottom: 12 }}>
              {picked.basis}
            </div>
          )}

          {picked.external_load_only && (
            <div className="faint" style={{ marginBottom: 12 }}>
              Это движение с весом тела: цель считается по весу, который ты довешиваешь
              сверху. Ноль — чистые подтягивания.
            </div>
          )}

          <div className="faint" style={{ marginBottom: 6 }}>Срок — если он у тебя есть</div>
          {/* Единственное место в приложении с системным вводом: календарь всё
              равно лучше, чем шаговый ввод по трём числам. Нижнюю границу даёт
              сервер (дата из свежего `/today`) — своей «сегодня» клиент не
              выдумывает, а вчерашнюю из кеша сюда не пускает. */}
          <input
            className="goal-date"
            type="date"
            value={date}
            min={minDate ?? undefined}
            onChange={(e) => setDate(e.target.value)}
          />

          {conflicting ? (
            <div className="flag warning" style={{ marginTop: 14 }}>
              По движению «{nameOf(conflicting.exercise_id)}» уже стоит цель{' '}
              {formatWeight(conflicting.target_1rm)} кг. Новая её закроет — прежняя уйдёт
              в историю как цель, от которой отказались.
            </div>
          ) : (
            <div className="faint" style={{ marginTop: 14 }}>
              Открытая цель по упражнению может быть одна: новая цель на то же движение
              закрывает предыдущую.
            </div>
          )}

          {/* Иначе человек поставит цель на бицепс, не увидит изменений в
              плане и решит, что цель ничего не значит. Значит — но с момента
              пересборки: переписывать уже начатый мезоцикл цель не вправе. */}
          <div className="faint" style={{ marginTop: 8 }}>
            Цель влияет и на программу: при следующей пересборке движок поставит это
            движение в план и перестанет менять его на соседние.
          </div>

          {error && <div className="flag critical" style={{ marginTop: 10 }}>{error}</div>}

          <button
            className="btn primary wide big"
            style={{ marginTop: 14 }}
            disabled={busy}
            onClick={() => void submit()}
          >
            {busy ? 'Сохраняю…' : 'Поставить цель'}
          </button>
        </>
      )}
    </div>
  )
}

/**
 * Строка движения в выборе цели.
 *
 * Текущий максимум показывается прямо здесь: именно он отвечает на вопрос
 * «а с чего я начинаю». Движение без истории не прячется — цель на то, чего
 * человек ещё не делал, законна, — но и не притворяется измеренным.
 */
function MovementRow({
  option,
  selected,
  onPick,
}: {
  option: GoalOption
  selected: boolean
  onPick: () => void
}) {
  return (
    <button type="button" className={`goal-move${selected ? ' selected' : ''}`} onClick={onPick}>
      <span className="goal-move-name">
        {option.name}
        {option.is_custom && <span className="pill">своё</span>}
        {option.has_open_goal && <span className="pill accent">цель стоит</span>}
      </span>
      <span className="faint tnum">
        {option.current_e1rm === null
          ? 'нет истории'
          : `сейчас ${formatWeight(option.current_e1rm)} кг`}
      </span>
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/* Мелочи                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Дата с годом, когда он не текущий.
 *
 * `formatDate` даёт «14 марта» — для прогноза этого мало: выход на вес часто
 * приходится на следующий год, и без года дата читается как ближайшая.
 */
function formatGoalDate(iso: string): string {
  const base = formatDate(iso)
  // «сегодня», «вчера», «завтра» — в них года нет по смыслу, а 31 декабря
  // приписка превратила бы их в «завтра 2027». Относительные ответы
  // `formatDate` отличаются от дат тем, что в них нет ни одной цифры.
  if (!/\d/.test(base)) return base
  const year = Number(iso.slice(0, 4))
  return Number.isFinite(year) && year !== new Date().getFullYear()
    ? `${base} ${year}`
    : base
}

/**
 * Текст ошибки для пользователя.
 *
 * Разница между «нет связи» и «сервер отказал» здесь принципиальна: цель, в
 * отличие от подхода, в офлайн-очередь не попадает — при обрыве связи она
 * просто не сохранена, и человек должен это понимать.
 */
function describe(e: unknown, fallback: string): string {
  if (e instanceof NetworkError) return 'Нет связи — цель не сохранена. Попробуй ещё раз.'
  if (e instanceof Error && e.message) return e.message
  return fallback
}
